// EventBridge event shapes — the second async write channel (D18). Transcribed
// from `contracts-legacy/notifications-backend/schemas.ts:1301-1501`. A port that
// reproduced all four SQS queues perfectly but dropped this bus would silently
// lose the whole product-analytics feed; the write closure therefore enumerates
// TABLES, QUEUES *and* EVENT BUSES.

import { z } from 'zod';

import { UUID_V4 } from './shared.js';

/**
 * The `Detail` envelope, shared by both event types. Built at
 * `product_analytics_events.py:120-135`.
 */
const domainEventDetail = <T extends z.ZodRawShape>(extra: T) =>
  z
    .object({
      /** `str(uuid.uuid4())` at `:127`. */
      eventId: z.string().regex(UUID_V4),
      /** `f'notification:{id}:sent'` / `:opened'` at `:46` / `:66`. */
      originIdempotencyKey: z.string(),
      /**
       * The INTERNAL org id from the Organizations GSI, never the Clerk id (`:129`).
       *
       * Pinned `z.string()` deliberately — RESIDUAL (R14): a DynamoDB `BOOL true` is
       * the one unclosable gap; the value is read off a row in the Organizations
       * table, which the legacy repo does not own. Left narrow on purpose, unlike the
       * row passthroughs.
       */
      organizationId: z.string(),
      /** `_now_iso()` at `:130` — `isoformat(timespec='milliseconds')` with `+00:00` → `Z`. */
      occurredAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/),
      notificationId: z.string(),
      /** Hard-coded `'in_app'` at `:49` and `:99`. */
      channel: z.literal('in_app'),
      /**
       * `_template(action_type, entity_type)` at `:226-228` (D21): the source
       * guarantees ONLY that the result EQUALS ITS OWN LOWERCASE (every part passes
       * through `.lower()`, and `':'` has no case). Nothing strips a colon INSIDE a
       * part and the `if part` filter runs BEFORE `.strip()`, so segment counts and
       * empty segments are NOT pinned — do not tighten this to a segment-count regex.
       *
       * R4 GREPTILE REPAIR — the previous `.min(1)` pin ("_template never returns
       * empty") was a DERIVED PIN NARROWER THAN ITS INPUT, the exact defect class
       * above. The all-falsy branch does return `'notification'`, but a surviving
       * part contributes the separator only when ANOTHER part follows it: the
       * `if part` filter keeps a whitespace-only string (`'  '` is truthy in
       * Python), which then strips to `''`, and `':'.join([''])` is `''`. Executed
       * against the real function: `_template('  ', None)` → `''`. The open inbound
       * contract (`notificationMessageAsConsumedSchema`) accepts exactly that source
       * value — `actionType` is `pythonTruthy` (`'  '` passes), `entityType` is
       * `z.unknown()` — so the empty template is EMITTABLE on this channel and the
       * port must admit it. Do NOT re-pin non-emptiness here.
       */
      template: z
        .string()
        .refine((t) => t === t.toLowerCase(), {
          message: 'every part passes through .lower(), so _template output equals its own lowercase',
        }),
      /** Hard-coded `'notifications-backend'` at `:51` and `:104`. */
      provider: z.literal('notifications-backend'),
      /**
       * D20: `z.unknown()` + not-null, NOT `z.string()`. The value is the SAME
       * `clerkUserID` the consumer schema deliberately admits as `z.unknown()`,
       * observed one hop later with no validation at any hop — `7`, `true`, `["x"]`
       * and `{"k":1}` are all genuinely emittable.
       *
       * ABSENCE IS DELIBERATE AND REACHABLE — do NOT "fix" this into a required key.
       * `_strip_none` (`:131`, `:231-238`) REMOVES a key whose value is `None`, and
       * both publishers can pass `None`. Hence ABSENT-or-any-value, and NEVER
       * present-and-null; `z.unknown()` is implicitly optional in Zod 3, which is the
       * correct semantics here.
       */
      userId: z.unknown().refine((v) => v !== null, {
        message: '_strip_none removes a None userId, so the key is ABSENT, never present-and-null',
      }),
      ...extra,
    })
    .strict();

/**
 * `notification.sent.v1` — `product_analytics_events.py:14`, fired from
 * `notifications_writer/handler.py:51` on EVERY notification written.
 */
export const notificationSentEventSchema = z
  .object({
    Source: z.literal('charitystack.notifications'),
    DetailType: z.literal('notification.sent.v1'),
    /**
     * D-EVENTBUS-NAME-IS-TWO-VALUED (`schemas.ts:1428-1452`): the stage is
     * COLLAPSED, not interpolated — `'prod'` iff `app.stage === 'prod'`, else
     * `'dev'`. Staging and every personal stage publish to the DEV bus. Pinning an
     * interpolated `charitystack-<stage>` here would let a ported worker emit a bus
     * that DOES NOT EXIST and drop every staging event in silence.
     */
    EventBusName: z.enum(['charitystack-dev', 'charitystack-prod']),
    Detail: domainEventDetail({}),
  })
  .strict();

export type NotificationSentEvent = z.infer<typeof notificationSentEventSchema>;

/**
 * `notification.opened.v1` — `product_analytics_events.py:15`, fired from
 * `mark_read/handler.py:57` (single mark, only when the row WAS unread) and `:77`
 * (read-all, in EventBridge's ten-entry batches).
 */
export const notificationOpenedEventSchema = z
  .object({
    Source: z.literal('charitystack.notifications'),
    DetailType: z.literal('notification.opened.v1'),
    /** Two-valued — see `notificationSentEventSchema`. */
    EventBusName: z.enum(['charitystack-dev', 'charitystack-prod']),
    Detail: domainEventDetail({}),
  })
  .strict();

export type NotificationOpenedEvent = z.infer<typeof notificationOpenedEventSchema>;

/**
 * The event-bus counterpart to `QUEUE_CONFIG` (`schemas.ts:1473-1501`).
 */
export const EVENT_BUS_CONFIG = {
  ProductAnalyticsBus: {
    /**
     * NOT a template — the stage is collapsed to two values before interpolation
     * (`stacks/ApiStack.ts:34-38`).
     */
    nameRule: "prod iff app.stage === 'prod', else dev — ApiStack.ts:34-38",
    names: ['charitystack-dev', 'charitystack-prod'],
    source: 'charitystack.notifications',
    detailTypes: ['notification.sent.v1', 'notification.opened.v1'],
    /** Both publishers, per `stacks/ApiStack.ts:555` and `:616`. */
    publishers: ['notificationsWriter', 'markRead'],
    /** `_put_eventbridge_entries:91-92` — EventBridge's own ten-entry cap. */
    maxBatchEntries: 10,
    /** Every failure path returns False and is swallowed; see D18. */
    bestEffort: true,
    /** `:139-142` — an unset/blank EVENT_BUS_NAME skips the publish and logs info. */
    skipsWhenBusNameUnset: true,
  },
} as const;
